import { IonAlert } from '@ionic/react';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';

export interface BackButtonConfirmationAlertReturnType {
  alert: ReactElement;
  openAlert: (options?: BackButtonConfirmationOptions) => void;
}

export interface BackButtonConfirmationOptions {
  onClose?: (willNavigate: boolean) => void;
}

export const useBackButtonConfirmationAlert =
  (): BackButtonConfirmationAlertReturnType => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [alertOptions, setAlertOptions] = useState<
      BackButtonConfirmationOptions | undefined
    >();

    const handleClose = useCallback(
      (shouldNavigate: boolean) => {
        if (alertOptions?.onClose) {
          alertOptions.onClose(shouldNavigate);
        }

        if (shouldNavigate) {
          window.history.back();
        }

        setIsOpen(false);

        return true;
      },
      [alertOptions]
    );

    const alert = useMemo(() => {
      return (
        <IonAlert
          header={getLocalizedText('common:discard')}
          message={getLocalizedText('common:discard_confirmation')}
          isOpen={isOpen}
          buttons={[
            {
              text: getLocalizedText('common:discard') || 'Discard',
              handler: () => handleClose(true),
              role: 'destructive',
            },
            {
              text: getLocalizedText('common:cancel') || 'Cancel',
              handler: () => handleClose(false),
            },
          ]}
          backdropDismiss={false}
        />
      );
    }, [isOpen, handleClose]);

    const openAlert = useCallback((options?: BackButtonConfirmationOptions) => {
      setAlertOptions(options);
      setIsOpen(true);
    }, []);

    const backButtonConfirmationReturn: BackButtonConfirmationAlertReturnType =
      useMemo(() => {
        return {
          alert,
          openAlert,
        };
      }, [alert, openAlert]);

    return backButtonConfirmationReturn;
  };
